import { computed, onBeforeUnmount, ref } from 'vue'

export type TransferPhase = 'idle' | 'uploading' | 'done'

// Placeholder until uploads go through the API, which will return the real link.
const SHARE_URL = 'https://dropway.app/t/k7Qx92'
const TICK_MS = 120
const UNITS = ['B', 'KB', 'MB', 'GB']

export function formatBytes(bytes: number): string {
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024
    unit++
  }
  return `${unit ? value.toFixed(1) : value} ${UNITS[unit]}`
}

/**
 * State machine behind the transfer card: pick files, send them, get a link.
 * The upload is simulated; replace `transfer` with a service call to make it real.
 */
export function useFileTransfer() {
  const files = ref<File[]>([])
  const phase = ref<TransferPhase>('idle')
  const progress = ref(0)
  const copied = ref(false)
  let timer: ReturnType<typeof setInterval> | undefined

  const summary = computed(() => {
    const count = files.value.length
    const total = files.value.reduce((sum, file) => sum + file.size, 0)
    return `${count} file${count === 1 ? '' : 's'} · ${formatBytes(total)}`
  })

  function addFiles(list: FileList | null | undefined) {
    if (list?.length) files.value.push(...Array.from(list))
  }

  function removeFile(index: number) {
    files.value.splice(index, 1)
  }

  function stopTimer() {
    clearInterval(timer)
    timer = undefined
  }

  function transfer() {
    if (!files.value.length || phase.value !== 'idle') return
    phase.value = 'uploading'
    progress.value = 0
    timer = setInterval(() => {
      progress.value = Math.min(100, progress.value + 4 + Math.round(Math.random() * 6))
      if (progress.value >= 100) {
        stopTimer()
        phase.value = 'done'
      }
    }, TICK_MS)
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(SHARE_URL)
      copied.value = true
    } catch {
      // The clipboard needs a secure context and the user's permission; keep the label as is.
    }
  }

  function reset() {
    stopTimer()
    files.value = []
    phase.value = 'idle'
    progress.value = 0
    copied.value = false
  }

  onBeforeUnmount(stopTimer)

  return {
    files,
    phase,
    progress,
    copied,
    summary,
    shareUrl: SHARE_URL,
    addFiles,
    removeFile,
    transfer,
    copyLink,
    reset,
  }
}
